import { create, enumToJson, fromJson } from "@bufbuild/protobuf";
import { get } from "svelte/store";
import { AssetType, AssetTypeSchema, BundleType, ConversionType, ConversionTypeSchema, ExperimentType, ExperimentTypeSchema, ExternalDestinationSchema, RenderMode, RenderModeSchema, type AssetRef, type AssetRefJson, type ExperimentJson, type ExternalDestinationJson, type LandingPageJson, type Link, type PageContainerJson, type Suite } from "#gen/bundles/v1/bundle_pb.js";
import type { Batch } from "#gen/codes/v3/codes_pb.js";
import type { EntrypointBatch } from "#gen/entrypoints/v1/entrypoints_pb.js";
import { PageSchema, type Component, type Page } from "#gen/pages/v2/page_pb.js";
import type { StaticAssetJson } from "#gen/staticassets/v1/static_assets_pb.js";
import { AssetState, type AssetLoad, type ConfigType } from "#lib/api/bundles.js";
import { DEFAULT_URL } from "#lib/components/flow/editor/builder/constants.js";
import { NON_DELETABLE_TYPES, NON_SELECTABLE_TYPES, RESTRICTED_ANCHORS, RESTRICTED_SOURCES, canConfigureEdge, isSplitType } from "#lib/components/flow/editor/builder/edgeRules.js";
import { getIconFromUrl } from "#lib/components/flow/editor/builder/properties/external/utils.js";
import { MODULE_PADDING, NODE_SPACING } from "#lib/components/flow/editor/builder/spacing.js";
import { SelectionType, type Selection, type TypeSelection } from "#lib/components/flow/editor/builder/types.js";
import type { ComponentDefMap } from "#lib/components/page/registry.js";
import { APP_ANCHOR_NAME, ENTRY_ASSET_TYPES, type BundleDag, type BundleNode } from "#lib/dag.js";
import { assetIconProvider, type IconAsset } from "#lib/iconProvider.js";
import { sentenceCase } from "#lib/string.js";
import { TokenProvider } from "#lib/tokenProvider.js";
import { getShortNameFromUrl } from "#lib/url.js";
import type { Edge, Node } from "@xyflow/svelte";
import type { ElkEdgeSection } from "elkjs/lib/elk.bundled";

export const PREVIEW_PREFIX = "preview-";

interface Size {
	width: number;
	height: number;
}

const INITIAL_NODE_DIMENSIONS: Size = {
	width: 440,
	height: 800,
};

export function isPreviewNode(id: string | null | undefined): boolean {
	return id?.startsWith(PREVIEW_PREFIX) ?? false;
}

export function convertPreviewNodeId(id: string | null | undefined): string {
	return id?.replace(PREVIEW_PREFIX, "") ?? "";
}

export function getRenderMode(dag: BundleDag<AssetRef>, key: string, ref: AssetRef | undefined): RenderMode {
	if (dag.isModuleRoot(key)) {
		const moduleKey = dag.getContainingModule(key);
		if (moduleKey) {
			const moduleNode = dag.getNode(moduleKey);
			if (moduleNode) {
				return moduleNode.ref.renderMode;
			}
		}
	}
	return ref?.renderMode || RenderMode.MODAL;
}

export function makeEdgeId(link: Link): string {
	return `${link.source}-${link.destination}`;
}

function estimateModuleSize(dag: BundleDag<AssetRef>, key: string): Size {
	const module = dag.getModule(key);
	if (module) {
		const nodesByLevel = module.getNodesByLevel();
		const order = Object.keys(nodesByLevel).length;
		const depth = Object.values(nodesByLevel).reduce((max, nodes) => Math.max(max, nodes.length), 0);
		return {
			width: INITIAL_NODE_DIMENSIONS.width * order + (order - 1) * NODE_SPACING + MODULE_PADDING * 2,
			height: INITIAL_NODE_DIMENSIONS.height * depth + (depth - 1) * NODE_SPACING + MODULE_PADDING * 2,
		};
	}
	return INITIAL_NODE_DIMENSIONS;
}

export function isSelectionInDag(selection: Selection, orderedNodes: BundleNode<AssetRef>[]): boolean {
	const id = selection.item.value.id;
	if (selection.item.type === SelectionType.NODE) {
		return orderedNodes.some(node => node.key === id);
	}
	return orderedNodes.some(node => node.linksOut.some(link => makeEdgeId(link) === id));
}

export function createNode(dag: BundleDag<AssetRef>, key: string, ref: AssetRef, parent?: string, existing?: Node, selected?: boolean, entrySelectable?: boolean): Node {
	const size = ref.type === AssetType.MODULE ? estimateModuleSize(dag, key) : INITIAL_NODE_DIMENSIONS;
	return {
		id: key,
		parentId: parent,
		extent: parent ? "parent" : undefined,
		type: AssetType[ref.type],
		data: { ...ref },
		position: existing?.position ?? { x: 0, y: 0 },
		deletable: !parent && !ENTRY_ASSET_TYPES.has(ref.type) && !NON_DELETABLE_TYPES.has(ref.type),
		draggable: false,
		connectable: !parent,
		selectable: !((!entrySelectable && ENTRY_ASSET_TYPES.has(ref.type)) || NON_SELECTABLE_TYPES.has(ref.type)),
		selected,
		initialWidth: size.width,
		initialHeight: size.height,
		// The anchor column overflows past the page node's right edge. Elevate so the anchors and
		// their highlight overlay paint above sibling destination nodes.
		zIndex: ref.type === AssetType.PAGE_V2 ? 1 : undefined,
	};
}

export function createEdge(dag: BundleDag<AssetRef>, link: Link, appHandleIndex: number, source?: BundleNode<AssetRef>, existing?: Edge, selected?: boolean): Edge {
	const sourceAssetType = source?.ref.type ?? AssetType.UNSPECIFIED;
	const baseSelectable = !RESTRICTED_SOURCES.has(sourceAssetType) && !RESTRICTED_ANCHORS.has(link.sourceAnchor);
	const sourceHandle = link.sourceAnchor === APP_ANCHOR_NAME ? `${link.sourceAnchor}-${appHandleIndex}` : link.sourceAnchor;
	const targetHandle = link.destinationAnchor;
	// Carry the route the last layout computed, so a rebuild does not drop every ported edge to a
	// smoothstep curve until the next layout lands. The id is only source and destination, so the
	// anchors can move under it and a route drawn for the old ports would take the wrong corridor.
	const sameAnchors = existing !== undefined && existing.sourceHandle === sourceHandle && existing.targetHandle === targetHandle;
	return {
		id: makeEdgeId(link),
		type: "custom",
		source: link.source,
		target: link.destination,
		sourceHandle,
		targetHandle,
		data: { ...link, elkSections: sameAnchors ? (existing.data as { elkSections?: ElkEdgeSection[] } | undefined)?.elkSections : undefined },
		selectable: baseSelectable && canConfigureEdge(dag, source, link),
		interactionWidth: link.conditions.length > 0 ? 80 : undefined,
		selected,
	};
}

export function createBlankPage(suite: Suite, key: string): Page {
	return create(PageSchema, {
		orgId: suite.orgId,
		workspaceId: suite.workspaceId,
		brandKitId: suite.brandKitId,
		name: getNameFromKey(key),
		folderPath: suite.folderPath ?? "",
		state: AssetState.DRAFT,
		components: [],
		tags: [],
	});
}

export function getTypeIcon(type: AssetType, config?: ConfigType): IconAsset {
	if (type === AssetType.EXTERNAL_DESTINATION && config) {
		const external = fromJson(ExternalDestinationSchema, config);
		return getIconFromUrl(external.type);
	}
	return assetIconProvider.get(type) ?? { icon: "question-mark", iconSet: "heroicons" };
}

export function getDisplayName(key: string, node?: BundleNode<AssetRef>): string {
	if (!node) {
		return getNameFromKey(key);
	}
	if (node.ref.displayName) {
		return node.ref.displayName;
	}
	if (ENTRY_ASSET_TYPES.has(node.ref.type)) {
		return "Entry";
	}
	const parts = key.split(":");
	return getNameFromKey(parts[parts.length - 1]);
}

export function getNameFromKey(key: string): string {
	const name = key.replaceAll("suite_ref", "flow").replaceAll("static_asset", "file").replaceAll("policy_ref", "terms & conditions");
	return sentenceCase(name.replace(/_/g, " ").replace(/-/g, " "));
}

export function getPageV2ComponentEditorTitle(comp: Component, defs: ComponentDefMap): string {
	const def = comp.typeUri ? defs[comp.typeUri] : undefined;
	const name = (def?.displayName ?? def?.name)?.trim().toLowerCase();
	return name ? `Edit ${name}` : "Edit component";
}

export function initialSize(type: AssetType): { width: number; height: number } {
	switch (type) {
		case AssetType.CODE_V3:
		case AssetType.CODE_BATCH:
		case AssetType.ENTRYPOINT_BATCH:
		case AssetType.EXPERIMENT:
		case AssetType.DECISION:
		case AssetType.AGENT:
		case AssetType.LOOKUP:
			return { width: 80, height: 80 };
		case AssetType.AD_UNIT:
		case AssetType.GOTO:
		case AssetType.SUITE_REF:
			return { width: 380, height: 82 };
		case AssetType.POLICY_REF:
		case AssetType.MODULE:
		case AssetType.PAGE_V2:
		case AssetType.EXIT:
		case AssetType.EXTERNAL_DESTINATION:
		case AssetType.LANDING_PAGE:
		case AssetType.PAGE_CONTAINER:
		case AssetType.STATIC_ASSET:
			return { width: 440, height: 782 };
		default:
			return { width: 0, height: 0 };
	}
}

export function createAssetRefForSelection(bundleType: BundleType, selection: TypeSelection, key: string): AssetRefJson {
	const config = selection.config ?? defaultConfig(bundleType, selection.type);
	const ref: AssetRefJson = {
		displayName: getNameFromKey(key),
		type: enumToJson(AssetTypeSchema, selection.type),
		config: config,
	};
	if (selection.type === AssetType.MODULE) {
		ref.displayName = selection.template?.name;
		ref.description = selection.template?.description;
		ref.iconUrl = selection.template?.iconUrl;
	} else if (selection.type === AssetType.EXTERNAL_DESTINATION) {
		ref.shortName = getShortNameFromUrl((config as ExternalDestinationJson).url ?? "");
		ref.conversionType = enumToJson(ConversionTypeSchema, ConversionType.EXTERNAL_PIXEL);
	} else if (selection.type === AssetType.STATIC_ASSET) {
		ref.shortName = "Download file";
		ref.conversionType = enumToJson(ConversionTypeSchema, ConversionType.FILE_DOWNLOADED);
	} else if (selection.type === AssetType.PAGE_CONTAINER) {
		ref.shortName = "View my page";
	} else if (selection.type === AssetType.POLICY_REF) {
		ref.shortName = "Terms & conditions";
	} else if (selection.type === AssetType.PAGE_V2) {
		ref.displayName = "Page";
	} else if (selection.type === AssetType.AD_UNIT) {
		ref.renderMode = enumToJson(RenderModeSchema, RenderMode.INLINE);
	}
	return ref;
}

export function defaultConfig(bundleType: BundleType, type: AssetType): ConfigType | undefined {
	switch (type) {
		case AssetType.LANDING_PAGE:
			return {
				layoutName: bundleType === BundleType.CONTACT ? "modern" : "legacy",
				primaryText: "",
				images: [],
			} as LandingPageJson;
		case AssetType.PAGE_CONTAINER:
			return {
				layoutName: "blank",
			} as PageContainerJson;
		case AssetType.EXPERIMENT:
			return {
				experimentType: enumToJson(ExperimentTypeSchema, ExperimentType.BANDIT),
				banditExperimentData: {
					defaultAssetKey: "",
				},
			} as ExperimentJson;
		case AssetType.EXTERNAL_DESTINATION:
			return {
				url: DEFAULT_URL,
			} as ExternalDestinationJson;
		case AssetType.STATIC_ASSET:
			return {} as StaticAssetJson;
		default:
			return {};
	}
}

export function isDisabled(dag: BundleDag<AssetRef>, node: BundleNode<AssetRef> | undefined): boolean {
	const queue: BundleNode<AssetRef>[] = [];
	if (node) {
		if (node.parent && dag.getNode(node.parent)) {
			queue.push(dag.getNode(node.parent)!);
		} else {
			queue.push(node);
		}
	}
	const visited: Set<string> = new Set();
	while (queue.length) {
		const current = queue.shift()!;
		if (!visited.has(current.key)) {
			visited.add(current.key);
			if (current.ref.disabled) {
				return true;
			}
			for (const neighbor of current.linksIn) {
				const neighborNode = dag.getNode(neighbor.source);
				if (neighborNode) {
					queue.push(neighborNode);
				}
			}
		}
	}
	return false;
}

export function calculatePreviewHeight(dag: BundleDag<AssetRef>, id: string): number {
	let sum = 120;
	let enabledCount = 0;
	for (const app of dag.findModulesFromAssetKey(id, APP_ANCHOR_NAME)) {
		const asset = app.ref;
		if (asset.disabled) {
			continue;
		}
		let renderMode = asset.renderMode;
		if (isSplitType(asset.type)) {
			if (app.linksOut.length > 0) {
				const node = dag.getNode(app.linksOut[0].destination);
				if (node) {
					renderMode = node.ref.renderMode;
				}
			}
		}
		enabledCount++;
		switch (renderMode) {
			case RenderMode.UNSPECIFIED:
			case RenderMode.MODAL:
				sum += 55;
				break;
			case RenderMode.IMAGE:
				sum += 159;
				break;
			case RenderMode.THUMBNAIL:
				sum += 198;
				break;
			// Inline case -- always overflow
			case RenderMode.INLINE:
				return 419;
			default:
				break;
		}
	}

	// Padding math
	if (enabledCount > 1) {
		sum += 14.3 * (enabledCount - 1);
	}

	return sum;
}

export function cloneTokenProviderForNode(tokenProvider: TokenProvider, dag: BundleDag<AssetRef>, assets: Record<string, AssetLoad>, id: string): TokenProvider {
	if (!tokenProvider) {
		return new TokenProvider();
	}
	const provider = tokenProvider.clone();
	addSourceRequestElements(provider, dag, assets, id);
	return provider;
}

export function addDataPropertyElements(provider: TokenProvider, dag: BundleDag<AssetRef>, assets: Record<string, AssetLoad>, entrypointBatchReferences: Record<string, EntrypointBatch>) {
	dag.findAllEntryPoints().forEach(node => {
		const asset = assets[node.key];
		if (!asset) {
			return;
		}
		let batch: Batch | EntrypointBatch | undefined = undefined;
		let external = false;
		if (asset.assetType() === AssetType.ENTRYPOINT_BATCH || asset.assetType() === AssetType.CODE_BATCH) {
			const store = asset.asCodeBatch();
			if (!store) {
				return;
			}
			batch = get(store);
		} else if (asset.assetType() === AssetType.ENTRYPOINT_BATCH_REF) {
			const store = asset.asEntrypointBatchRef();
			if (!store) {
				return;
			}
			const entrypointBatchId = get(store)?.entryPointBatchId;
			batch = entrypointBatchReferences[entrypointBatchId ?? ""];
			external = true;
		}
		if (batch) {
			const schema = batch.metadataSchema ? JSON.parse(batch.metadataSchema) : { type: "object", properties: {}, additionalProperties: false };
			provider.addMetadataElements(batch, schema, external);
		}
	});
}

function addSourceRequestElements(provider: TokenProvider, dag: BundleDag<AssetRef>, assets: Record<string, AssetLoad>, key: string) {
	let node = dag.getNode(key);
	if (node?.parent && dag.isModuleRoot(node.key)) {
		node = dag.getNode(node.parent);
	}
	node?.linksIn.forEach(link => {
		addRequestElements(provider, dag, assets, link.source);
	});
}

function addRequestElements(provider: TokenProvider, dag: BundleDag<AssetRef>, assets: Record<string, AssetLoad>, key: string) {
	function add(source: BundleNode<AssetRef>, key: string) {
		const asset = assets[key];
		if (!asset) {
			return;
		}
		if (asset.asAgent()) {
			provider.addAgentElements(source.ref, get(asset.asAgent()!));
		} else if (asset.asLookup()) {
			provider.addLookupElements(source.ref, get(asset.asLookup()!));
		} else if (asset.asPage()) {
			provider.addPageDataElements(source.ref, get(asset.asPage()!), get(asset.data()));
		}
	}
	const source = dag.getNode(key);
	if (!source) {
		return;
	}
	if (source.ref.type === AssetType.DECISION || source.ref.type === AssetType.EXPERIMENT) {
		addSourceRequestElements(provider, dag, assets, key);
	} else if (source.ref.type !== AssetType.MODULE) {
		add(source, key);
	} else {
		const module = dag.getModule(key);
		module?.nodes.entries().forEach(([key, node]) => {
			if (node.linksOut.length === 0) {
				add(source, key);
			}
		});
	}
}

export function hasPageTray(selection: Selection | undefined): boolean {
	if (selection?.item.type !== SelectionType.NODE) {
		return false;
	}
	const node = selection.item.value;
	return (node.data as AssetRef)?.type === AssetType.PAGE_V2 && !node.parentId;
}
